'use client'

import { Button } from '@heroui/button'
import { Input } from '@heroui/input'
import { Modal, ModalContent, ModalBody, useDisclosure } from '@heroui/modal'
import Image from 'next/image'
import { useEffect, useState } from 'react'

import Locked from '@/assets/svgs/lock-illustration.svg'
import { EyeFilledIcon, EyeSlashFilledIcon } from '@/components/shared'
import { Typography } from '@/components/ui'
import { useSignOut } from '@/hooks'
import {
  useConfigStoreHydrated,
  useEncryptionKey,
  useSetEncryptionKey,
} from '@/store'
import { decrypt } from '@/utils'

interface PasswordCheckerProps {
  encriptedContent: string
}

const PasswordChecker = ({ encriptedContent }: PasswordCheckerProps) => {
  const { mutate: signOut, isPending: isSignOutPending } = useSignOut()
  const encryptionKey = useEncryptionKey()
  const hasHydrated = useConfigStoreHydrated()
  const setEncryptionKey = useSetEncryptionKey()
  const [showPassword, setShowPassword] = useState(false)
  const [checkingPassword, setCheckingPassword] = useState(false)
  const [isInvalidPassword, setIsInvalidPassword] = useState(false)
  const [password, setPassword] = useState('')
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure()

  const handleButtonClick = async () => {
    try {
      setCheckingPassword(true)
      await decrypt(encriptedContent, password, true)
      setEncryptionKey(password)
      onClose()
    } catch (error) {
      console.log('error', error)
      setIsInvalidPassword(true)
    } finally {
      setCheckingPassword(false)
    }
  }

  const handleSignOut = () => {
    signOut()
  }

  const toggleVisibility = () => {
    setShowPassword(!showPassword)
  }

  useEffect(() => {
    const checkPassword = async () => {
      if (!hasHydrated) return
      try {
        if (!encryptionKey) {
          console.log('no encryption key')
          return onOpen()
        }
        await decrypt(encriptedContent, encryptionKey, true)
      } catch (error) {
        console.log('error', error)
        onOpen()
      }
    }
    checkPassword()
  }, [encriptedContent, onOpen, encryptionKey, hasHydrated])

  return (
    <Modal
      size="full"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      hideCloseButton
      scrollBehavior="inside"
      isDismissable={false}
      placement="center"
      isKeyboardDismissDisabled={false}
      classNames={{
        base: 'max-h-dvh h-full my-0',
        body: 'py-0 mt-0',
      }}
    >
      <ModalContent>
        <ModalBody>
          <div className="h-full w-full px-4">
            <div className="flex h-full w-full flex-col items-center justify-center gap-10 py-5">
              <Image src={Locked} alt="Moments" width={120} />
              <div className="flex flex-col gap-2">
                <Typography
                  size="2xlarge"
                  weight="regular"
                  className="text-center font-crimsonPro text-default-foreground"
                >
                  Account Password
                </Typography>
                <Typography
                  size="small"
                  className="text-center text-content4-foreground"
                >
                  Let’s decrypt your account
                </Typography>
              </div>
              <Input
                placeholder="Introduce password"
                className="max-w-[400px]"
                size="lg"
                variant="bordered"
                type={showPassword ? 'text' : 'password'}
                value={password}
                isInvalid={isInvalidPassword}
                onChange={(e) => setPassword(e.target.value)}
                errorMessage="Invalid password"
                classNames={{
                  inputWrapper: 'h-16',
                  input: 'placeholder:text-content4-foreground',
                }}
                endContent={
                  <button
                    className="focus:outline-none"
                    type="button"
                    onClick={toggleVisibility}
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? (
                      <EyeSlashFilledIcon className="pointer-events-none text-2xl text-default-400" />
                    ) : (
                      <EyeFilledIcon className="pointer-events-none text-2xl text-default-400" />
                    )}
                  </button>
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleButtonClick()
                  }
                }}
              />
              <div className="flex flex-col gap-4">
                <Button
                  size="lg"
                  variant="solid"
                  onPress={handleButtonClick}
                  color="primary"
                  className="rounded-[80px]"
                  disabled={checkingPassword || isSignOutPending}
                >
                  Unlock account
                </Button>
                <Button
                  data-hover="false"
                  variant="light"
                  color="default"
                  disableRipple
                  disableAnimation
                  onPress={handleSignOut}
                  disabled={isSignOutPending}
                >
                  Sign out
                </Button>
              </div>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default PasswordChecker
